import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { TranslationJson } from 'ember-intl/-private/utils/translations';
import type RouterService from '@ember/routing/router-service';
import type Session from '../services/session';
import type Theme from '../services/theme';
import type ShareIntentService from '../services/share-intent';
import type PushNotificationsService from '../services/push-notifications';
// @ts-expect-error - Virtual module provided by @ember-intl/vite plugin at runtime
import translationsEnUs from 'virtual:ember-intl/translations/en-us';

export default class ApplicationRoute extends Route {
  @service declare session: Session;
  @service declare theme: Theme;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare shareIntent: ShareIntentService;
  @service declare pushNotifications: PushNotificationsService;

  async beforeModel() {
    this.intl.addTranslations('en-us', translationsEnUs as TranslationJson);
    this.intl.setLocale(['en-us']);

    // Initialize theme service early to load saved theme
    void this.theme;

    // Start session setup WITHOUT awaiting — share intent detection runs in
    // parallel so the drawer can appear before the session round-trip finishes.
    const sessionSetup = this.session.setup();
    this.shareIntent.sessionSetupPromise = sessionSetup;

    // Wait for push-notification and share-intent initializers to finish their
    // native calls before making navigation decisions.
    // A 500 ms safety timeout ensures non-Capacitor builds are never blocked.
    await Promise.race([
      Promise.all([
        this.pushNotifications.readyPromise,
        this.shareIntent.readyPromise,
      ]),
      new Promise<void>((r) => setTimeout(r, 500)),
    ]);

    // If a share intent arrived, navigate directly to the share drawer immediately
    // — without waiting for session.setup() to finish. The share component awaits
    // sessionSetupPromise internally once the drawer is already visible.
    if (this.shareIntent.pendingUrl || this.shareIntent.pendingImageUri) {
      this.router.transitionTo('share');
      return;
    }

    // For all other routes, ensure session is fully initialized before routing.
    await sessionSetup;

    // Navigate to the source page if a notification was tapped on cold start
    // and the router wasn't ready when pushNotificationActionPerformed fired.
    if (
      this.pushNotifications.pendingSourceId &&
      this.session.isAuthenticated
    ) {
      const id = this.pushNotifications.pendingSourceId;
      this.pushNotifications.pendingSourceId = null;
      this.router.transitionTo(`/sources/${id}`);
      return;
    }

    // Global auth gate: redirect unauthenticated users to login
    if (!this.session.isAuthenticated) {
      const target = this.router.currentURL;
      // Allow login/auto-login routes through (avoid infinite redirect loop)
      if (
        !target ||
        (!target.startsWith('/login') && !target.startsWith('/auto-login'))
      ) {
        // Save intended destination so login can redirect back
        if (target && target !== '/') {
          localStorage.setItem('favoris:login-redirect', target);
        }
        this.router.transitionTo('login');
      }
    }
  }
}
